import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import { Directions as DirectionsIcon } from "@material-ui/icons";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import RouteModal from "../RouteModal";

// Bug conhecido do react-leaflet com bundlers tipo webpack/CRA: o icone
// padrao do Leaflet (L.Icon.Default) tenta resolver um caminho relativo
// que nao existe no build final, e o patch usual (mergeOptions no
// prototype) nao pega de forma confiavel dependendo de como o bundler
// resolve a instancia do modulo "leaflet". Pra nao depender disso,
// define os dois icones (normal/selecionado) explicitamente aqui e
// passa SEMPRE um `icon` pra cada Marker abaixo - nunca deixa o Marker
// cair no default interno do Leaflet.
const defaultIcon = new L.Icon({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const selectedIcon = new L.Icon({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [30, 49],
  iconAnchor: [15, 49],
  popupAnchor: [1, -40],
  shadowSize: [49, 49],
  className: "voicechat-selected-marker",
});

// Ajusta o zoom/centro do mapa automaticamente pra caber todos os
// alfinetes na tela assim que eles chegam - sem isso o mapa abriria
// centrado em algum lugar arbitrario.
const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lon], 14);
      return;
    }
    map.fitBounds(
      points.map((p) => [p.lat, p.lon]),
      { padding: [30, 30] }
    );
  }, [points, map]);
  return null;
};

// "Ver todos no mapa" - pedido do usuario 2026-09-18: clicar num botao e
// ver todos os leads como alfinete pra selecionar visualmente quais
// entram na rota, em vez de marcar checkbox por checkbox numa tabela.
// Usa Leaflet + OpenStreetMap (GRATUITO, sem API paga do Google so' pra
// mostrar o mapa) - geocodifica os enderecos via Nominatim (tambem
// gratuito, 1 req/s) no backend (GeocodePointsService) antes de
// desenhar os alfinetes. Clicar num alfinete alterna selecionado/nao;
// "Criar rota com selecionados" abre o RouteModal que ja existe,
// passando lat/lon ja' conhecidos pra nao geocodificar de novo.
const MapSelectionModal = ({ open, onClose, points, boardId }) => {
  const [loading, setLoading] = useState(false);
  const [geocoded, setGeocoded] = useState([]);
  const [selectedIndexes, setSelectedIndexes] = useState([]);
  const [routeModalOpen, setRouteModalOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const fetchGeocoded = async () => {
      setLoading(true);
      setGeocoded([]);
      setSelectedIndexes([]);
      try {
        const { data } = await api.post("/route-optimization/geocode-points", { points });
        setGeocoded(data.points || []);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchGeocoded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleSelected = (idx) => {
    setSelectedIndexes((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };

  const handleClose = () => {
    setSelectedIndexes([]);
    onClose();
  };

  const selectedPoints = selectedIndexes.map((idx) => geocoded[idx]);

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Leads no mapa
          {geocoded.length > 0 && ` (${geocoded.length} de ${points.length} localizados)`}
        </DialogTitle>
        <DialogContent style={{ padding: 0, height: 500 }}>
          {loading ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
            >
              <CircularProgress />
              <Typography variant="caption" color="textSecondary" style={{ marginTop: 12 }}>
                Localizando endereços no mapa (pode levar alguns segundos)...
              </Typography>
            </Box>
          ) : geocoded.length === 0 ? (
            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
              <Typography color="textSecondary">
                Nenhum endereço pôde ser localizado no mapa.
              </Typography>
            </Box>
          ) : (
            <MapContainer
              center={[geocoded[0].lat, geocoded[0].lon]}
              zoom={12}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FitBounds points={geocoded} />
              {geocoded.map((p, idx) => {
                const selected = selectedIndexes.includes(idx);
                return (
                  <Marker
                    key={p.cardId || idx}
                    position={[p.lat, p.lon]}
                    icon={selected ? selectedIcon : defaultIcon}
                    eventHandlers={{ click: () => toggleSelected(idx) }}
                  >
                    <Popup>
                      <b>{p.label}</b>
                      <br />
                      {p.address}
                      <br />
                      <span style={{ color: selected ? "#2e7d32" : "#9e9e9e", fontSize: "0.85em" }}>
                        {selected ? "Selecionado ✓ (clique pra remover)" : "Clique no alfinete pra selecionar"}
                      </span>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Typography variant="body2" color="textSecondary" style={{ marginRight: "auto", paddingLeft: 8 }}>
            {selectedIndexes.length} selecionado(s)
          </Typography>
          <Button onClick={handleClose}>Fechar</Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<DirectionsIcon />}
            disabled={selectedIndexes.length === 0}
            onClick={() => setRouteModalOpen(true)}
          >
            Criar rota com selecionados
          </Button>
        </DialogActions>
      </Dialog>

      {routeModalOpen && (
        <RouteModal
          open={routeModalOpen}
          onClose={() => setRouteModalOpen(false)}
          boardId={boardId}
          points={selectedPoints.map((p) => ({
            cardId: p.cardId,
            label: p.label,
            address: p.address,
            lat: p.lat,
            lon: p.lon,
          }))}
        />
      )}
    </>
  );
};

export default MapSelectionModal;
